import React, { useRef, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Tooltip,
} from '@mui/material';
import { OwnerProfile } from '../../../types/owner';
import {
    useCreateOwnerLogoMutation,
    useUpdateOwnerProfileMutation,
} from '../../../../app/Slices/SevenDayApiSlice';
import toast from '../../../shared/Toast';
import Cropper from 'react-easy-crop';
import { Area, usePhotoEditor } from '../Form/Profile Picture/usePhotoEditor';
import EditIcon from '@mui/icons-material/Edit';
import Avatar from '@mui/material/Avatar';
import Close from '@mui/icons-material/Close';

const OwnerLogoUpload = ({ owner }: { owner: OwnerProfile }) => {
    const [uploadOwnerLogo] = useCreateOwnerLogoMutation();
    const [updateOwnerProfile] = useUpdateOwnerProfileMutation();
    const inputRef = useRef<HTMLInputElement>(null);
    const [file, setFile] = useState<File>();
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);

    const { originalUrl, handleCrop, handleEdit } = usePhotoEditor(file);

    const handleClose = () => {
        setOpen(false);
        setFile(undefined);
        setZoom(1);
        setCrop({ x: 0, y: 0 });
    };

    const handleSave = async () => {
        try {
            const cropped = await handleEdit();
            if (!cropped) return;
            setSaving(true);

            const data = new FormData();
            data.append('file', cropped);

            const response = await uploadOwnerLogo({
                ownerId: owner.id,
                data,
            }).unwrap();

            await updateOwnerProfile({
                ownerId: owner.id,
                logo: { id: response.id },
            }).unwrap();

            toast.success('Logo updated successfully');
            handleClose();
        } catch {
            toast.error('Error uploading logo');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box>
            <Box
                sx={{
                    position: 'relative',
                    width: 125,
                    height: 125,
                    flexShrink: 0,
                }}
            >
                <Avatar
                    src={owner.logo?.src}
                    alt={`${owner.name} logo`}
                    sx={{
                        width: 125,
                        height: 125,
                        border: '1px solid',
                        borderColor: 'custom.cardActionBackground',
                    }}
                />
                <Tooltip title='Edit logo'>
                    <IconButton
                        size='small'
                        type='button'
                        aria-label='Edit logo'
                        onClick={() => inputRef.current?.click()}
                        sx={{
                            position: 'absolute',
                            right: 4,
                            bottom: 4,
                            bgcolor: 'background.paper',
                            border: 1,
                            borderColor: 'divider',
                            boxShadow: 1,
                            '&:hover': { bgcolor: 'background.paper' },
                        }}
                    >
                        <EditIcon
                            sx={{ fontSize: 18, color: 'text.secondary' }}
                        />
                    </IconButton>
                </Tooltip>
                <input
                    ref={inputRef}
                    type='file'
                    hidden
                    accept='image/png,image/jpeg,image/jpg'
                    onChange={(e) => {
                        const next = e.target.files?.[0];
                        e.target.value = '';
                        if (!next) return;
                        setFile(next);
                        setOpen(true);
                    }}
                />
            </Box>
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth='xs'>
                <DialogTitle>
                    Crop Logo
                    <IconButton
                        color='primary'
                        aria-label='Close'
                        onClick={handleClose}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ position: 'relative', height: 320 }}>
                        {originalUrl.current && (
                            <Cropper
                                image={originalUrl.current}
                                crop={crop}
                                zoom={zoom}
                                aspect={1}
                                cropShape='round'
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={(_: Area, area: Area) =>
                                    handleCrop(area)
                                }
                            />
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button
                        variant='contained'
                        onClick={handleSave}
                        disabled={saving}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default OwnerLogoUpload;
